/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { View, Icon, Avatar } from '@components/atoms'
import { Drawer as MuiDrawer } from '@mui/material'

import { useNavigate, useLocation } from 'react-router-dom'
import { useAppDispatch, useAppSelector, configActions } from '@redux/index'
import { HomeNavMenuType } from '@redux/types'
import { CustomerType } from '@schemas'
import config, { isExternalUri, FORUM_URL, forumEntryUrl } from '@configs'
import { mono, monoFont } from './monolith.tokens'

interface PropsType {
	menuList: HomeNavMenuType[]
}

/**
 * 모바일 드로어 — Monolith(2안). 890px 이하에서 MonolithNav 의 햄버거가 여는 화면이다.
 * 기존 구현은 `drawer/HomeDrawer.tsx` 에 그대로 남아 있다(MonolithNav ↔ HomeNav 와 같은 보관 방식).
 *
 * 구 드로어는 가운데 정렬 버튼 목록에 계정 영역만 grey[900] 검정 판이었다 — 네비·푸터가
 * Monolith 로 바뀐 뒤로 열 때마다 옛 화면이 나왔다. 여기서는 §5 문법 그대로:
 * 좌측 정렬 헤어라인 행, 상위 메뉴는 크게(300) 하위는 작게(faint), 검정 판 없음.
 *
 * 항목 SSOT 는 그대로 `config.homeMenu()` · `productMenu()` · `supportMenu()` · `customerMenu()` 다.
 * 앵커(`/products#kedem`) 처리도 구 드로어와 같은 방식으로 남긴다.
 * Forum 은 사이트 밖(forum.judahs.io)이라 라우터를 타지 않고 주소창을 바꾼다 — 네비와 같다.
 */
export const MonolithDrawer = (props: PropsType) => {
	const { menuList } = props

	// hook
	const navigate = useNavigate()
	const location = useLocation()

	// redux dispatch
	const reduxDispatch = useAppDispatch()

	// redux state
	const isHomeOpenDrawer: boolean = useAppSelector(state => state.config.isHomeOpenDrawer)
	const isLogedin: boolean = useAppSelector(state => state.customerAuth.isLogedin)
	const customer: CustomerType = useAppSelector(state => state.customerAuth.customer)

	const customerMenuList: HomeNavMenuType[] = config.customerMenu(customer.roles)
	// 2026-09-09 사이트맵 §9: 데스크톱은 드롭다운이 없고, 하위 메뉴는 이 드로어에서만 펼친다.
	const subMenuObj: { [id: string]: HomeNavMenuType[] } = {
		products: config.productMenu(),
		support: config.supportMenu(),
	}

	const onToggleClick = React.useCallback(() => {
		reduxDispatch(configActions.IS_HOME_OPEN_DRAWER(!isHomeOpenDrawer))
	}, [isHomeOpenDrawer])

	const onMenuPress = (aMenu: HomeNavMenuType) => {
		onToggleClick()
		if (isExternalUri(aMenu.uri)) {
			// 포럼은 로그인돼 있으면 로그인된 채로 착지한다 (FORUM_PLAN §11)
			window.location.href = aMenu.uri === FORUM_URL ? forumEntryUrl() : aMenu.uri
			return
		}
		navigate(aMenu.uri)
		// 같은 페이지 안의 앵커(/products#kedem)는 라우트가 안 바뀌어 스크롤이 안 일어난다 — 직접 내린다.
		const hashIdx = aMenu.uri.indexOf('#')
		if (hashIdx !== -1) {
			const id = aMenu.uri.slice(hashIdx + 1)
			window.setTimeout(() => {
				document.getElementById(id)?.scrollIntoView({ block: 'start' })
			}, 350)
		}
	}

	// 네비와 같은 판정 — 상세(/products/{code})는 상위 항목을 켠다. 앵커 항목은 켜지 않는다.
	const isActive = (uri: string): boolean => {
		if (isExternalUri(uri)) return false
		if (uri.includes('#')) return false
		if (uri === '/') return location.pathname === '/'
		return location.pathname === uri || location.pathname.startsWith(`${uri}/`)
	}

	// 메뉴 라벨은 네비와 같이 첫 글자만 대문자.
	const toTitle = (s: string) =>
		s.length === 0 ? s : s[0].toUpperCase() + s.slice(1).toLowerCase()

	return (
		<MuiDrawer
			css={style}
			open={isHomeOpenDrawer}
			anchor="right"
			onClose={onToggleClick}
			variant="temporary"
		>
			<View className="mnDrawerInner">
				<View className="mnDrawerTop">
					<button
						type="button"
						className="mnDrawerClose"
						onClick={onToggleClick}
						aria-label="Close menu"
					>
						<Icon name="close" css={{ fontSize: 24 }} />
					</button>
				</View>

				<nav className="mnDrawerMenu" aria-label="Main">
					{menuList.map(aMenu => {
						const subList = subMenuObj[aMenu.id] ?? []

						return (
							<React.Fragment key={aMenu.id}>
								<button
									type="button"
									className={`mnDrawerItem ${isActive(aMenu.uri) ? 'on' : ''}`}
									aria-current={isActive(aMenu.uri) ? 'page' : undefined}
									onClick={() => onMenuPress(aMenu)}
								>
									{toTitle(aMenu.title)}
								</button>

								{subList.map(aSub => (
									<button
										key={aSub.id}
										type="button"
										className="mnDrawerItem mnDrawerSubItem"
										onClick={() => onMenuPress(aSub)}
									>
										{aSub.title}
									</button>
								))}
							</React.Fragment>
						)
					})}
				</nav>

				<View className="mnDrawerAccount">
					{isLogedin ? (
						<View className="mnDrawerUser">
							<Avatar
								picture={customer.profileImageUrl}
								name={customer.name}
								color={customer.profileColor === '' ? mono.ink : customer.profileColor}
								size={36}
								extpandingDisable={true}
							/>
							<View className="mnDrawerUserText">
								<span className="mnDrawerUserName">{customer.name}</span>
								<span className="mnDrawerUserEmail">{customer.email}</span>
							</View>
						</View>
					) : (
						<span className="mnDrawerLabel">Account</span>
					)}

					{isLogedin ? (
						customerMenuList.map(aMenu => (
							<button
								key={aMenu.id}
								type="button"
								className={`mnDrawerItem mnDrawerSubItem ${
									isActive(aMenu.uri) ? 'on' : ''
								}`}
								onClick={() => onMenuPress(aMenu)}
							>
								{aMenu.title}
							</button>
						))
					) : (
						<button
							type="button"
							className="mnDrawerItem mnDrawerSubItem"
							onClick={() =>
								onMenuPress({ id: 'login', title: 'Login', uri: '/customer/login' })
							}
						>
							Login
						</button>
					)}
				</View>
			</View>
		</MuiDrawer>
	)
}

const style = css({
	'& .MuiDrawer-paper': {
		width: 'min(340px, 86vw)',
		background: mono.bg,
		borderLeft: `1px solid ${mono.line}`,
		boxShadow: 'none',
		fontFamily: monoFont.sans,
	},

	'.mnDrawerInner': {
		display: 'flex',
		flexDirection: 'column',
		height: '100%',
		padding: '0 28px 40px',
		boxSizing: 'border-box',
	},

	/** 네비와 같은 72 높이로 열려서 햄버거 자리에 닫기가 그대로 온다 */
	'.mnDrawerTop': {
		display: 'flex',
		justifyContent: 'flex-end',
		alignItems: 'center',
		height: 72,
		flexShrink: 0,

		'.mnDrawerClose': {
			display: 'flex',
			alignItems: 'center',
			padding: 0,
			border: 0,
			background: 'transparent',
			color: mono.ink,
			cursor: 'pointer',
		},
	},

	'.mnDrawerMenu': {
		display: 'flex',
		flexDirection: 'column',
		marginTop: 28,
	},

	'.mnDrawerItem': {
		display: 'block',
		width: '100%',
		padding: '18px 0',
		border: 0,
		borderBottom: `1px solid ${mono.line}`,
		background: 'transparent',
		fontFamily: monoFont.sans,
		fontSize: 22,
		fontWeight: 300,
		lineHeight: 1.1,
		color: mono.ink,
		textAlign: 'left',
		cursor: 'pointer',
		transition: 'color 200ms ease-out',

		'&.on': {
			fontWeight: 400,
		},

		'&.mnDrawerSubItem': {
			padding: '13px 0',
			fontSize: 14,
			fontWeight: 300,
			color: mono.dim,

			'&:hover': {
				color: mono.ink,
			},
			'&.on': {
				color: mono.ink,
				fontWeight: 500,
			},
		},
	},

	/** 계정은 목록 아래에 붙인다 — 검정 판(구 드로어)을 쓰지 않는다 */
	'.mnDrawerAccount': {
		display: 'flex',
		flexDirection: 'column',
		marginTop: 'auto',
		paddingTop: 36,

		'.mnDrawerLabel': {
			fontSize: 12,
			letterSpacing: '.2em',
			textTransform: 'uppercase',
			color: mono.faint,
			fontWeight: 500,
			paddingBottom: 6,
		},

		/** 로그인 상태에서는 'ACCOUNT' 라벨 자리에 본인 아바타와 이름이 온다 */
		'.mnDrawerUser': {
			display: 'flex',
			alignItems: 'center',
			gap: 12,
			paddingBottom: 14,

			'.mnDrawerUserText': {
				display: 'flex',
				flexDirection: 'column',
				gap: 3,
				minWidth: 0,
			},

			'.mnDrawerUserName': {
				fontSize: 15,
				fontWeight: 400,
				color: mono.ink,
				overflow: 'hidden',
				textOverflow: 'ellipsis',
				whiteSpace: 'nowrap',
			},

			'.mnDrawerUserEmail': {
				fontSize: 12,
				fontWeight: 300,
				color: mono.faint,
				overflow: 'hidden',
				textOverflow: 'ellipsis',
				whiteSpace: 'nowrap',
			},
		},
	},

	'@media (prefers-reduced-motion: reduce)': {
		'.mnDrawerItem': { transition: 'none' },
	},
})
